import React, {useState} from "react";
import {Button, Card, CardBody, Col, Progress, Tooltip} from "reactstrap";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ORDER_STATUSES} from "../constants/order";
import usePermissions from "../../../../hooks/usePermissions";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import UserListAvatar from "../../../../components/UI/userListAvatar";
import {toast} from "react-toastify";

interface CardOrderProps {
    data: any;
    activeTabId: string;
    toggleAssignOrder?: (id: string) => void;
    toggleAssignIntegra?: (id: string) => void;
    toggleInfoOrder?: (id: string) => void;
    toggleUpdate?: (id: string) => void;
    toggleChangeStatus?: (id: string) => void;
}

const CardOrder: React.FC<CardOrderProps> = ({
                                                 data,
                                                 activeTabId,
                                                 toggleAssignOrder,
                                                 toggleAssignIntegra,
                                                 toggleInfoOrder,
                                                 toggleUpdate,
                                                 toggleChangeStatus
                                             }) => {

    const queryClient = useQueryClient();
    const {hasPermission} = usePermissions();

    const [tooltipOpen, setTooltipOpen] = useState(false);

    const activeCapa = data.current_capas ?? data.waiting_capas ?? data.not_active_capas;

    const capa_day = activeCapa?.capa_day ?? 0;
    const order_day = activeCapa?.date ?? null;
    const order_status = activeCapa?.status ?? data.capa_dates?.at(-1)?.status ?? null;

    const status = ORDER_STATUSES.find((status) => status.value === order_status);

    const {mutate: pinTrafficOrder, isPending: isPendingPin} = useMutation({
        mutationFn: () => TrafficOrderService.pinnedTrafficOrder(data.id),
        onSuccess: () => queryClient.invalidateQueries({queryKey: ["keyTrafficOrders"]}),
        onError: (error) => {
            console.error("Error pinning traffic order:", error);
            toast.error("Failed to pin order");
        },
    });

    const formatTime = (time: string) => {
        if (!time) return "";
        const [hours, minutes] = time.split(":");
        return `${hours}:${minutes}`;
    };

    const changeStatusHandler = () => {
        if (toggleChangeStatus) toggleChangeStatus(data.id);
    };
    const pinOrderHandler = () => {
        if (!isPendingPin) pinTrafficOrder();
    };

    const toggle = () => setTooltipOpen(!tooltipOpen);

    const isPinnedClass = data.is_pinned ? "mdi-pin-off" : "mdi-pin";
    const isPendingPinClass = isPendingPin ? "mdi-loading spin-right" : isPinnedClass;

    const tooltipId = `Tooltip-${activeTabId}-${data.id}`;

    return (
        <>
            <Col xxl="2" xl="3" md="4" sm="6" className="d-flex">
                <Card className="w-100 border border-2 text-center">
                    <CardBody className="d-flex flex-column pb-2">
                        <div className="w-100 btn-group justify-content-between mb-2" role="group">
                            {hasPermission("traffic-order-pinned") && activeTabId !== "3" && (
                                <div style={{marginLeft: "-8px", marginTop: "-8px"}} className="d-flex">
                                    <Button color="secondary" outline size="sm"
                                            onClick={pinOrderHandler}
                                            disabled={isPendingPin}>
                                        <i className={`mdi ${isPendingPinClass} font-size-12`}/>
                                    </Button>
                                </div>
                            )}
                        </div>

                        <h5 className="font-size-16 mb-2">{data.broker?.name}</h5>

                        <div className="position-relative w-100 mb-1">
                            <Progress
                                striped
                                max={data?.daily_cap || 0}
                                value={capa_day}
                                color="success"
                                style={{height: "20px"}}
                            >
                                <span className="position-absolute w-100 text-center text-white"
                                      style={{lineHeight: "20px"}}>
                                    {capa_day}/{data?.daily_cap || 0}
                                </span>
                            </Progress>
                        </div>

                        {(data.countries && data.country_box) && (
                            <div className="font-size-12 mb-1">
                                {data.countries?.map((i: any) => {
                                    return <span key={i.id}>{i.iso} </span>;
                                })}
                                {data.country_box?.map((i: any) => {
                                    return <span key={i.id}>{i.name} </span>;
                                })}
                            </div>
                        )}

                        <div>
                            <button type="button" className={`border-0 badge ${status?.colorClass || "bg-secondary"}`}
                                    onClick={changeStatusHandler}
                                    disabled={!toggleChangeStatus}>
                                <i className={`bx ${status?.iconClass || "bx-info-circle"} font-size-16 align-middle me-2`}/>
                                {status?.label || "Unknown"}
                            </button>
                        </div>

                        <div>
                            <span className={`badge ${data.broker_id ? "bg-success" : "bg-danger"}`}>
                                <i className={`bx ${data.broker_id ? "bx-check-double" : "bx-block"} font-size-16 align-middle me-2`}/>
                                {data.broker_id ? "Integrated" : "Not Integrated"}
                            </span>
                        </div>

                        {(data.from_time || data.to_time) && (
                            <div className="font-size-14 fw-bold mt-2">
                                {data.from_time && (
                                    <span>From: {formatTime(data.from_time)}
                                </span>)}
                                {data.from_time && data.to_time && " - "}
                                {data.to_time &&
                                    <span> To: {formatTime(data.to_time)}
                                </span>}
                            </div>
                        )}

                        <div className="mt-2 mb-1">
                            {data.responsible_users && <UserListAvatar responsible_users={data.responsible_users}/>}
                        </div>

                        {data.pay_method_label && (
                            <div className="font-size-14"><strong>Pay method:</strong> {data.pay_method_label}</div>
                        )}

                        {data.funnel_name && (
                            <div className="font-size-14"><strong>Funnel name:</strong> {data.funnel_name}</div>
                        )}

                        <div className="mt-auto">
                            <hr className="my-2"/>

                            <div className="font-size-10 fw-bold">
                                Date{data.capa_dates?.length > 0 && "s"}: {order_day}{" "}

                                {data.capa_dates?.length > 0 && (
                                    <span>
                                    <i className="mdi mdi-calendar-blank-multiple font-size-16 text-primary"
                                       id={tooltipId} style={{cursor: "pointer"}}/>
                                    <Tooltip
                                        placement="bottom"
                                        isOpen={tooltipOpen}
                                        target={tooltipId}
                                        toggle={toggle}
                                        delay={{show: 100, hide: 150}}>
                                        {data.capa_dates?.map((item: any) => item.date).join(", ")}
                                    </Tooltip>
                            </span>)}
                            </div>
                        </div>
                    </CardBody>

                    <div className="btn-group flex-wrap pe-1" role="group">
                        {hasPermission("assign-traffic-order") && toggleAssignOrder && (
                            <button
                                type="button"
                                className="btn btn-outline-light rounded-1 p-1 my-1 ms-1"
                                title="Assign"
                                onClick={() => toggleAssignOrder(data.id)}
                            >
                                <i className="mdi mdi-plus-circle font-size-16"/>
                            </button>
                        )}
                        {hasPermission("view-info-traffic-order") && toggleInfoOrder && (
                            <button
                                type="button"
                                onClick={() => toggleInfoOrder(data.id)}
                                className="btn btn-outline-light rounded-1 p-1 my-1 ms-1"
                                title="Info"
                            >
                                <i className="mdi mdi-information font-size-16"/>
                            </button>
                        )}
                        {hasPermission("edit-traffic-order") && toggleUpdate && (
                            <button
                                type="button"
                                onClick={() => toggleUpdate(data.id)}
                                className="btn btn-outline-light rounded-1 p-1 my-1 ms-1"
                                title="Edit"
                            >
                                <i className="mdi mdi-pencil font-size-16"/>
                            </button>
                        )}
                        {hasPermission("integrated-traffic-order") && toggleAssignIntegra && (
                            <button
                                type="button"
                                onClick={() => toggleAssignIntegra(data.id)}
                                className="btn btn-outline-light rounded-1 p-1 my-1 ms-1"
                                title="Integrated"
                            >
                                <i className="mdi mdi-book-cog font-size-16"/>
                            </button>
                        )}
                    </div>
                </Card>
            </Col>
        </>
    );
};

export default React.memo(CardOrder);
